/**
 * Pure helpers for building the `updateSalesOrder` mutation input from the Sales Order
 * screen's basic fields. Kept free of Svelte/Houdini so the save behaviour can be unit-tested
 * without rendering the component or mocking the GraphQL client.
 */

/** The four basic fields (plus the always-present internal comments) the screen can edit. */
export interface SalesOrderBasicFieldsSnapshot {
	internalComments: string | null
	externalComments: string | null
	/** Stored as epoch milliseconds so comparisons are value-based, not Date-reference-based. */
	date: number | null
	truckNumber: string | null
	purchaseOrderNumber: string | null
}

/** The current, possibly-edited values read off the live sales order. */
export interface SalesOrderBasicFieldsCurrent {
	internalComments: string | null | undefined
	externalComments: string | null | undefined
	date: Date | null | undefined
	truckNumber: string | null | undefined
	purchaseOrderNumber: string | null | undefined
}

/**
 * The mutation input we send. Structurally a subset of Houdini's generated `SalesOrderUpdate`
 * (every optional field is omitted unless it changed). Declared locally so tests don't depend on
 * generated `$houdini` types.
 */
export interface SalesOrderBasicFieldsInput {
	storeId: number
	salesOrderId: number
	internalComments?: string
	externalComments?: string
	truckNumber?: string
	purchaseOrderNumber?: string
	date?: Date
}

/** Capture the basic fields as loaded, so we can later tell which the user actually changed. */
export function snapshotSalesOrderBasicFields(current: SalesOrderBasicFieldsCurrent): SalesOrderBasicFieldsSnapshot {
	return {
		internalComments: current.internalComments ?? null,
		externalComments: current.externalComments ?? null,
		date: current.date ? current.date.getTime() : null,
		truckNumber: current.truckNumber ?? null,
		purchaseOrderNumber: current.purchaseOrderNumber ?? null,
	}
}

/**
 * Build the `updateSalesOrder` input, including only the fields the user actually changed.
 *
 * Sending only changed fields is required for correctness, not just tidiness: the API requires the
 * separate `SALES_ORDERS_CAN_CHANGE_DATE` permission whenever a non-null `date` is supplied, so
 * sending `date` on every save would break a comment-only save for a user who can edit but cannot
 * change the date. Omitting an unchanged field is equivalent to sending `null` (the API ignores it
 * rather than clearing the value). `date` is NOT NULL and can never be cleared, so it is only ever
 * sent when it changes to a real value. Every value is passed through verbatim — no trimming,
 * casing, or other transformation.
 */
export function buildSalesOrderUpdateInput(
	key: { storeId: number; salesOrderId: number },
	original: SalesOrderBasicFieldsSnapshot,
	current: SalesOrderBasicFieldsCurrent,
): SalesOrderBasicFieldsInput {
	const input: SalesOrderBasicFieldsInput = {
		storeId: key.storeId,
		salesOrderId: key.salesOrderId,
	}

	if ((current.internalComments ?? null) !== original.internalComments) {
		input.internalComments = current.internalComments ?? ''
	}
	if ((current.externalComments ?? null) !== original.externalComments) {
		input.externalComments = current.externalComments ?? ''
	}
	if ((current.truckNumber ?? null) !== original.truckNumber) {
		input.truckNumber = current.truckNumber ?? ''
	}
	if ((current.purchaseOrderNumber ?? null) !== original.purchaseOrderNumber) {
		input.purchaseOrderNumber = current.purchaseOrderNumber ?? ''
	}

	const currentDate = current.date ? current.date.getTime() : null
	if (currentDate !== null && currentDate !== original.date) {
		input.date = current.date ?? undefined
	}

	return input
}
